'use client';

import React, { useState, useEffect } from 'react';
import { useMediaQuery } from '@/hooks/use-media-query';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from '@/components/ui/dialog';
import {
	Drawer,
	DrawerContent,
	DrawerHeader,
	DrawerTitle,
	DrawerDescription,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useUser } from '@clerk/nextjs';
import { getPaystackConfig } from '@/lib/paystack';
import { RsvpResponseForm } from './RsvpResponseForm';
import { RsvpQuestion } from '@/lib/schemas/event';

// Import Paystack types
declare global {
	interface Window {
		PaystackPop: {
			setup(config: any): { openIframe(): void };
		};
	}
}

interface Ticket {
	id: string;
	name: string;
	price: number;
	remaining_quantity: number;
}

interface PurchaseTicketDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	ticket: Ticket;
	eventId: string;
}

export function PurchaseTicketDialog({
	open,
	onOpenChange,
	ticket,
	eventId,
}: PurchaseTicketDialogProps) {
	const [quantity, setQuantity] = useState(1);
	const [isLoading, setIsLoading] = useState(false);
	const [isProcessingPayment, setIsProcessingPayment] = useState(false);
	const [rsvpQuestions, setRsvpQuestions] = useState<RsvpQuestion[]>([]);
	const [rsvpResponses, setRsvpResponses] = useState<Record<string, string>>(
		{}
	);
	const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
	const [isGuestCheckout, setIsGuestCheckout] = useState(false);
	const [guestEmail, setGuestEmail] = useState('');
	const [guestName, setGuestName] = useState('');
	const isDesktop = useMediaQuery('(min-width: 768px)');
	const router = useRouter();
	const { user } = useUser();
	const purchaseInProgressRef = React.useRef(false);

	// Ensure Paystack script is loaded
	useEffect(() => {
		const loadPaystackScript = () => {
			if (typeof window !== 'undefined' && !window.PaystackPop) {
				const script = document.createElement('script');
				script.src = 'https://js.paystack.co/v1/inline.js';
				script.async = true;
				document.head.appendChild(script);
			}
		};

		loadPaystackScript();
	}, []);

	// Reset purchase state when dialog opens/closes
	useEffect(() => {
		if (!open) {
			// Reset the purchase in progress flag when dialog closes
			purchaseInProgressRef.current = false;
		}
	}, [open]);

	// Fetch RSVP questions when dialog opens
	useEffect(() => {
		if (open && eventId) {
			setIsLoadingQuestions(true);

			// Add timeout to prevent infinite loading
			const timeoutId = setTimeout(() => {
				setIsLoadingQuestions(false);
			}, 10000); // 10 second timeout

			fetch(`/api/events/${eventId}/rsvp-questions`)
				.then((response) => response.json())
				.then((data) => {
					if (data.success) {
						setRsvpQuestions(data.questions || []);
					}
				})
				.catch((error) => {
					console.error('Error fetching RSVP questions:', error);
					setRsvpQuestions([]);
				})
				.finally(() => {
					clearTimeout(timeoutId);
					setIsLoadingQuestions(false);
				});
		}
	}, [open, eventId]);

	const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = parseInt(e.target.value);
		if (value > 0 && value <= ticket.remaining_quantity) {
			setQuantity(value);
		}
	};

	// Check if all required questions are answered
	const areRequiredQuestionsAnswered = () => {
		const requiredQuestions = rsvpQuestions.filter((q) => q.required);
		return requiredQuestions.every(
			(question) =>
				question.id &&
				rsvpResponses[question.id] &&
				rsvpResponses[question.id].trim() !== ''
		);
	};

	// Check if purchase button should be disabled
	const isPurchaseDisabled = () => {
		const hasRequiredGuestInfo =
			!isGuestCheckout || (guestEmail.trim() && guestName.trim());
		return (
			isLoading ||
			isLoadingQuestions ||
			!hasRequiredGuestInfo ||
			(rsvpQuestions.length > 0 && !areRequiredQuestionsAnswered())
		);
	};

	const handlePurchase = async () => {
		// Prevent double-submission
		if (purchaseInProgressRef.current) {
			console.warn('🚫 DUPLICATE REQUEST BLOCKED - Purchase already in progress');
			return;
		}
		
		console.log('🎫 Starting purchase process...', { 
			ticketId: ticket.id, 
			eventId, 
			quantity,
			ticketName: ticket.name 
		});

		// Validate authentication or guest checkout
		if (!isGuestCheckout && !user?.emailAddresses?.[0]?.emailAddress) {
			toast.error('Please sign in to purchase passes');
			return;
		}

		if (isGuestCheckout) {
			if (!guestEmail.trim()) {
				toast.error('Please enter your email address');
				return;
			}
			if (!guestName.trim()) {
				toast.error('Please enter your name');
				return;
			}
		}

		// Validate required RSVP questions
		const requiredQuestions = rsvpQuestions.filter((q) => q.required);
		for (const question of requiredQuestions) {
			if (
				question.id &&
				(!rsvpResponses[question.id] ||
					rsvpResponses[question.id].trim() === '')
			) {
				toast.error(
					`Please answer the required question: ${question.question}`
				);
				return;
			}
		}

		// Mark purchase as in progress
		purchaseInProgressRef.current = true;
		console.log('✅ Purchase marked as IN PROGRESS');
		setIsLoading(true);
		try {
			// 1. Create ticket purchase record
			console.log('📝 Creating purchase record...');
			const purchaseResponse = await fetch('/api/tickets/purchase', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					ticketId: ticket.id,
					eventId,
					quantity,
					totalPrice: ticket.price * quantity,
					rsvpResponses,
					...(isGuestCheckout
						? {
								guestEmail: guestEmail.trim(),
								guestName: guestName.trim(),
							}
						: {}),
				}),
			});

			const purchaseData = await purchaseResponse.json();
			console.log('✅ Purchase response received:', purchaseData);

			if (!purchaseResponse.ok) {
				throw new Error(
					purchaseData.error || 'Failed to create ticket purchase'
				);
			}

			if (!purchaseData.purchaseId) {
				throw new Error('No purchase ID returned from server');
			}

			// 2. If ticket is free, we're done
			if (ticket.price === 0) {
				// Update status for free tickets immediately
				await fetch(`/api/tickets/purchase/${purchaseData.purchaseId}`, {
					method: 'PATCH',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						payment_status: 'paid',
						status: 'completed',
					}),
				});

				toast.success('Your RSVP has been submitted!');
				onOpenChange(false);
				router.refresh();
				return;
			}

			// 3. Initialize payment
			console.log('Initializing payment...');

			// Generate unique reference for Paystack
			const reference = `vouch_${purchaseData.purchaseId}_${Date.now()}`;

			const paymentResponse = await fetch('/api/payments/initialize', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					email: isGuestCheckout
						? guestEmail.trim()
						: user?.emailAddresses?.[0]?.emailAddress,
					amount: ticket.price * quantity,
					reference,
					metadata: {
						purchase_id: purchaseData.purchaseId,
						event_id: eventId,
						ticket_id: ticket.id,
						quantity,
					},
				}),
			});

			const paymentData = await paymentResponse.json();
			console.log('Payment initialization response:', paymentData);

			if (!paymentResponse.ok) {
				throw new Error(paymentData.error || 'Failed to initialize payment');
			}

			// 4. Validate Paystack configuration
			const paystackKey = process.env.NEXT_PUBLIC_PAYSTACK_PUBLIC_KEY;
			console.log('Paystack key check:', paystackKey ? 'Found' : 'Missing');
			if (!paystackKey) {
				console.error(
					'Paystack public key is missing. Please check your .env.local file.'
				);
				throw new Error(
					'Payment system configuration error. Please refresh the page and try again.'
				);
			}

			const userEmail = isGuestCheckout
				? guestEmail.trim()
				: user?.emailAddresses?.[0]?.emailAddress;

			if (!userEmail) {
				throw new Error('Email address is required for payment');
			}

			// 5. Open Paystack popup
			const config = {
				key: paystackKey,
				email: userEmail,
				amount: ticket.price * quantity * 100,
				currency: 'GHS',
				ref: paymentData.data.reference,
				metadata: {
					purchase_id: purchaseData.purchaseId,
					event_id: eventId,
					ticket_id: ticket.id,
					quantity,
				},
				callback: function (response: any) {
					console.log('Payment callback received:', response);
					setIsProcessingPayment(true);

					// First create payment record
					fetch('/api/payments', {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							purchase_id: purchaseData.purchaseId,
							amount: ticket.price * quantity,
							paystack_reference: response.reference,
							paystack_transaction_id: response.trans,
							payment_method: 'card',
							payment_status:
								response.status === 'success' ? 'successful' : 'failed',
							metadata: {
								transaction: response.transaction,
								message: response.message,
								trxref: response.trxref,
								redirecturl: response.redirecturl,
							},
						}),
					})
						.then((response) => response.json())
						.then((data) => {
							console.log('Payment record created:', data);
							// Then update purchase status
							return fetch(`/api/tickets/purchase/${purchaseData.purchaseId}`, {
								method: 'PATCH',
								headers: {
									'Content-Type': 'application/json',
								},
								body: JSON.stringify({
									payment_status: 'paid',
									status: 'completed',
								}),
							});
						})
						.then((response) => response.json())
						.then((data) => {
							console.log('Status update response:', data);
							setIsProcessingPayment(false);
							toast.success(
								'Payment successful! Your confirmation email has been sent.'
							);
							onOpenChange(false);
							router.refresh();
						})
						.catch((error) => {
							console.error('Error processing payment:', error);
							setIsProcessingPayment(false);
							toast.error(
								'Payment recorded but status update failed. Please contact support.'
							);
						});
				},
				onClose: function () {
					console.log('Payment modal closed');
					setIsProcessingPayment(false);
					// Handle payment modal close
					fetch(`/api/tickets/purchase/${purchaseData.purchaseId}`, {
						method: 'PATCH',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							payment_status: 'unpaid',
							status: 'cancelled',
						}),
					})
						.then((response) => response.json())
						.then((data) => {
							console.log('Cancel status update response:', data);
							toast.error('Payment cancelled');
						})
						.catch((error) => {
							console.error('Error updating payment status:', error);
						});
				},
			};

			// Close the dialog before opening Paystack popup
			onOpenChange(false);

			// Function to wait for PaystackPop to be available
			const waitForPaystackPop = (retries = 10, delay = 100) => {
				return new Promise((resolve, reject) => {
					const checkPaystackPop = () => {
						if (typeof window !== 'undefined' && window.PaystackPop) {
							resolve(window.PaystackPop);
						} else if (retries > 0) {
							setTimeout(checkPaystackPop, delay);
							retries--;
						} else {
							reject(new Error('PaystackPop failed to load'));
						}
					};
					checkPaystackPop();
				});
			};

			// Wait for PaystackPop to be available, then open popup
			waitForPaystackPop()
				.then((PaystackPop: any) => {
					console.log('PaystackPop available, setting up payment...');
					console.log('Config:', config);

					const paystackPopup = PaystackPop.setup(config);

					if (!paystackPopup) {
						throw new Error(
							'Failed to setup Paystack popup - invalid configuration'
						);
					}

					console.log('Paystack popup setup successful, opening...');
					paystackPopup.openIframe();
				})
				.catch((error) => {
					console.error('PaystackPop error:', error);
					toast.error(
						`Payment system error: ${error.message || 'Please refresh the page and try again.'}`
					);
					setIsLoading(false);
				});
		} catch (error) {
			console.error('❌ Purchase error:', error);
			toast.error(
				error instanceof Error
					? error.message
					: 'Failed to process purchase. Please try again.'
			);
		} finally {
			console.log('🔄 Resetting purchase state');
			setIsLoading(false);
			purchaseInProgressRef.current = false;
		}
	};

	const Content = (
		<>
			<div className='space-y-4'>
				{/* Guest Checkout Toggle */}
				{!user && (
					<div className='p-4 border-2 border-dashed border-primary/20 rounded-lg bg-primary/5'>
						<div className='flex items-center space-x-2 mb-3'>
							<input
								type='checkbox'
								id='guest-checkout'
								checked={isGuestCheckout}
								onChange={(e) => setIsGuestCheckout(e.target.checked)}
								className='rounded border-primary'
							/>
							<Label
								htmlFor='guest-checkout'
								className='text-sm font-medium text-primary'
							>
								Continue as guest
							</Label>
						</div>
						<p className='text-xs text-muted-foreground'>
							Check this box to {ticket.price === 0 ? 'register' : 'purchase tickets'} without creating an account
						</p>
					</div>
				)}

				{/* Guest Information Form */}
				{isGuestCheckout && (
					<div className='space-y-3 p-4 border rounded-lg'>
						<h3 className='font-medium'>Guest Information</h3>
						<div>
							<Label htmlFor='guest-name'>Full Name</Label>
							<Input
								id='guest-name'
								type='text'
								placeholder='Enter your full name'
								value={guestName}
								onChange={(e) => setGuestName(e.target.value)}
								required
							/>
						</div>
						<div>
							<Label htmlFor='guest-email'>Email Address</Label>
							<Input
								id='guest-email'
								type='email'
								placeholder='Enter your email address'
								value={guestEmail}
								onChange={(e) => setGuestEmail(e.target.value)}
								required
							/>
						</div>
					</div>
				)}

				<div>
					<Label htmlFor='ticket-type'>
						{ticket.price === 0 ? 'Registration Type' : 'Ticket Type'}
					</Label>
					<p className='text-lg font-semibold'>{ticket.name}</p>
				</div>

				{/* Only show price for paid tickets */}
				{ticket.price > 0 && (
					<div>
						<Label htmlFor='price'>Price</Label>
						<p className='text-lg font-semibold'>GH₵ {ticket.price}</p>
					</div>
				)}

				<div>
					<Label htmlFor='quantity'>Quantity</Label>
					<Input
						id='quantity'
						type='number'
						min={1}
						max={ticket.remaining_quantity}
						value={quantity}
						onChange={handleQuantityChange}
					/>
					<p className='text-sm text-muted-foreground mt-1'>
						{ticket.remaining_quantity.toLocaleString()} tickets remaining
					</p>
				</div>

				{/* Only show total for paid tickets */}
				{ticket.price > 0 && (
					<div>
						<Label>Total</Label>
						<p className='text-xl font-bold'>
							GH₵ {(ticket.price * quantity).toFixed(2)}
						</p>
					</div>
				)}

				{/* RSVP Questions */}
				{isLoadingQuestions ? (
					<div className='mt-6 p-4 border rounded-lg'>
						<div className='flex items-center space-x-2'>
							<Loader2 className='h-4 w-4 animate-spin' />
							<span className='text-sm text-muted-foreground'>
								Loading questions...
							</span>
						</div>
					</div>
				) : rsvpQuestions.length > 0 ? (
					<div className='mt-6'>
						<RsvpResponseForm
							questions={rsvpQuestions}
							onResponsesChange={setRsvpResponses}
						/>
					</div>
				) : null}
			</div>

			<div className='mt-6'>
				<Button
					className='w-full'
					onClick={handlePurchase}
					disabled={isPurchaseDisabled()}
				>
					{isLoading ? (
						<>
							<Loader2 className='mr-2 h-4 w-4 animate-spin' />
							Processing...
						</>
					) : isLoadingQuestions ? (
						<>
							<Loader2 className='mr-2 h-4 w-4 animate-spin' />
							Loading...
						</>
					) : ticket.price === 0 ? (
						'Submit'
					) : isGuestCheckout ? (
						'Purchase as Guest'
					) : (
						'Purchase Ticket'
					)}
				</Button>
				{rsvpQuestions.length > 0 && !areRequiredQuestionsAnswered() && (
					<p className='text-sm text-muted-foreground mt-2 text-center'>
						Please answer all required questions to continue
					</p>
				)}
				{isGuestCheckout && (
					<p className='text-sm text-muted-foreground mt-2 text-center'>
						You'll receive a confirmation email with your ticket details
					</p>
				)}
			</div>
		</>
	);

	if (isDesktop) {
		return (
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className='max-h-[90vh] overflow-y-auto'>
					<DialogHeader>
						<DialogTitle>
							{ticket.price === 0 ? 'RSVP' : 'Purchase Ticket'}
						</DialogTitle>
						<DialogDescription>
							{ticket.price === 0
								? 'Complete your RSVP below.'
								: 'Complete your ticket purchase below.'}
						</DialogDescription>
					</DialogHeader>
					<div className='max-h-[70vh] overflow-y-auto pr-2 relative'>
						{Content}
						{isProcessingPayment && (
							<div className='absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50'>
								<div className='text-center space-y-4'>
									<div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto'></div>
									<p className='text-sm text-muted-foreground'>
										{ticket.price === 0
											? 'Submitting your RSVP...'
											: 'Processing your payment...'}
									</p>
									<p className='text-xs text-muted-foreground'>
										{ticket.price === 0
											? 'Please wait while we confirm your RSVP'
											: 'Please wait while we confirm your purchase'}
									</p>
								</div>
							</div>
						)}
					</div>
				</DialogContent>
			</Dialog>
		);
	}

	return (
		<Drawer open={open} onOpenChange={onOpenChange}>
			<DrawerContent className='max-h-[95vh]'>
				<DrawerHeader>
					<DrawerTitle>{ticket.price === 0 ? 'RSVP' : 'Purchase Ticket'}</DrawerTitle>
					<DrawerDescription>
						{ticket.price === 0
							? 'Complete your RSVP below.'
							: 'Complete your ticket purchase below.'}
					</DrawerDescription>
				</DrawerHeader>
				<div className='flex-1 overflow-y-auto px-4 pb-4 relative'>
					{Content}
					{isProcessingPayment && (
						<div className='absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50'>
							<div className='text-center space-y-4'>
								<div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto'></div>
								<p className='text-sm text-muted-foreground'>
									{ticket.price === 0
										? 'Submitting your RSVP...'
										: 'Processing your payment...'}
								</p>
								<p className='text-xs text-muted-foreground'>
									{ticket.price === 0
										? 'Please wait while we confirm your RSVP'
										: 'Please wait while we confirm your purchase'}
								</p>
							</div>
						</div>
					)}
				</div>
			</DrawerContent>
		</Drawer>
	);
}
